<template>
  <div 
    @dragover.prevent="isDragging = true" 
    @dragleave.prevent="isDragging = false" 
    @drop.prevent="handleDrop"
    :class="[
      'inline-block rounded-lg transition-all', 
      isDragging ? 'ring-2 ring-blue-500 ring-offset-2 opacity-80' : ''
    ]"
    :title="isDragging ? 'Drop file to upload' : ''"
  >
    <!-- Parsing Overlay -->
    <div v-if="isParsing" class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl flex flex-col items-center gap-4">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        <p class="text-lg font-semibold dark:text-white">{{ loadingText }}</p>
        <p class="text-sm text-gray-500">This may take up to 30 seconds.</p>
        <Button @click="cancelParsing" type="secondary">Cancel</Button>
      </div>
    </div>

    <input type="file" ref="fileInput" :accept="acceptTypes" class="hidden" @change="handleFileSelect" />
    <Tooltip :text="buttonText">
      <Button type="secondary" @click="$refs.fileInput.click()" class="flex items-center justify-center gap-2 !m-0 !p-2 md:!px-3 md:!py-2" :disabled="isParsing">
        <DocumentArrowUpIcon class="w-5 h-5 text-gray-600 dark:text-gray-300" />
        <span v-if="isDragging" class="hidden md:inline">Drop file here</span>
        <span v-else class="hidden md:inline">{{ buttonText }}</span>
      </Button>
    </Tooltip>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref } from 'vue'
import { DocumentArrowUpIcon } from '@heroicons/vue/24/outline'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import toastStore from '@/store/toast.store'

const props = defineProps({
  buttonText: {
    type: String,
    default: 'Extract from PDF/Image'
  },
  loadingText: {
    type: String,
    default: 'Parsing document with AI...'
  },
  parseUrl: {
    type: String,
    default: '/api/ai/parse-song'
  },
  acceptTypes: {
    type: String,
    default: 'image/*,application/pdf'
  }
})

const emit = defineEmits(['parsed', 'error'])

const isParsing = ref(false)
const isDragging = ref(false)
let abortController = null
const fileInput = ref(null)

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) processFile(file);
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer?.files[0];
  if (file) processFile(file);
};

const processFile = async (file) => {
  if (!file) return;

  // Optional: check file type against acceptTypes if strictly needed
  isParsing.value = true;
  abortController = new AbortController();
  
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(props.parseUrl, {
      method: 'POST',
      body: formData,
      signal: abortController.signal
    });
    
    if (response.ok) {
      const data = await response.json();
      emit('parsed', data);
      toastStore.dispatch('addMessage', {
        message: 'Successfully extracted!',
        icon: 'success',
        ttl: 3000
      });
    } else {
      const errData = await response.json();
      emit('error', errData);
      toastStore.dispatch('addMessage', {
        message: errData.error || 'Failed to parse file',
        icon: 'error',
        ttl: 4000
      });
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      toastStore.dispatch('addMessage', {
        message: 'Parsing cancelled',
        icon: 'info',
        ttl: 3000
      });
    } else {
      console.error('Parsing error:', error);
      emit('error', error);
      toastStore.dispatch('addMessage', {
        message: 'An error occurred during parsing',
        icon: 'error',
        ttl: 4000
      });
    }
  } finally {
    isParsing.value = false;
    abortController = null;
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const cancelParsing = () => {
  if (abortController) {
    abortController.abort();
  }
};
</script>
